if (!customElements.get('product-spec-card')) {
  customElements.define(
    'product-spec-card',
    class ProductSpecCard extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.section;
        this.variantCells = this.querySelectorAll('[data-spec-key]');
        this.productCells = this.querySelectorAll('.product-spec-card__cell:not(.product-spec-card__cell--variant)');
        this.container = this.querySelector('.product-spec-card__container');

        const jsonEl = this.querySelector(`#variant-specs-json-${this.sectionId}`);
        try {
          this.specData = JSON.parse(jsonEl?.textContent || '{}');
        } catch {
          this.specData = {};
        }

        this.onVariantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));

        const productInfo = this.closest('product-info');
        productInfo?.querySelector('input[name="id"]')?.addEventListener('change', this.onVariantInputChange.bind(this));

        // The server renders rows for the request's variant; the selected
        // variant can differ (e.g. history navigation restoring a form
        // state), so reconcile once on connect.
        this.syncFromCurrentVariant();
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber?.();
      }

      onVariantInputChange(event) {
        if (!this.closest('product-info')?.contains(event.target)) return;
        if (event.target.value) this.updateSpecs(event.target.value);
      }

      onVariantChange({ data: { sectionId, variant } }) {
        if (String(sectionId) !== String(this.sectionId)) return;
        if (variant?.id) {
          this.updateSpecs(variant.id);
          return;
        }
        this.syncFromCurrentVariant();
      }

      syncFromCurrentVariant() {
        const variantId =
          this.closest('product-info')?.querySelector('input[name="id"]')?.value ||
          new URLSearchParams(window.location.search).get('variant');

        if (variantId) this.updateSpecs(variantId);
        else this.updateCardVisibility();
      }

      formatValue(value) {
        if (value == null || value === '') return '';

        if (typeof value === 'object') {
          if (value.value != null && value.unit) {
            return `${value.value} ${value.unit}`.trim();
          }
          return String(value.value ?? value);
        }

        return String(value);
      }

      // Merchants often fill variant spec metafields on only one variant per
      // color group (e.g. the "1 Letter" variant of the pendant builders).
      // Fall back to a sibling variant with the same first option (color) so
      // specs stay stable while the count option changes. When the product
      // has a Length option, the sibling must also share the same length so
      // length-specific values (weight, total CTW) never leak across lengths.
      // Cells flagged data-no-fallback (metal, clarity, stones, center stone)
      // are genuinely per-variant and never borrow a sibling's value.
      resolveSpecValue(specs, key, allowFallback = true) {
        const direct = this.formatValue(specs[key]);
        if (direct) return direct;
        if (!allowFallback) return '';

        // Colour/metal option index comes from Liquid (resolved by option
        // name), so inserting or reordering options (e.g. Diamond Type) cannot
        // silently change which option is treated as the colour group.
        const colorIndex = parseInt(this.dataset.colorOptionIndex ?? '0', 10) || 0;
        const color = Array.isArray(specs.options) ? specs.options[colorIndex] : null;
        if (color == null) return '';

        const lengthIndex = parseInt(this.dataset.lengthOptionIndex ?? '-1', 10);

        for (const sibling of Object.values(this.specData)) {
          if (!Array.isArray(sibling.options) || sibling.options[colorIndex] !== color) continue;
          if (lengthIndex >= 0 && sibling.options[lengthIndex] !== specs.options[lengthIndex]) continue;
          const value = this.formatValue(sibling[key]);
          if (value) return value;
        }

        return '';
      }

      updateSpecs(variantId) {
        const specs = this.specData[String(variantId)] || {};

        this.variantCells.forEach((cell) => {
          const key = cell.dataset.specKey;
          const formatted = this.resolveSpecValue(specs, key, !cell.hasAttribute('data-no-fallback'));
          const valueEl = cell.querySelector('[data-spec-value]');
          const currentValue = valueEl?.textContent.trim() || '';
          const isVisible = !cell.hidden;

          if (formatted) {
            if (isVisible && currentValue === formatted) return;
            if (valueEl) valueEl.textContent = formatted;
            cell.hidden = false;
          } else {
            if (!isVisible && currentValue === '') return;
            if (valueEl) valueEl.textContent = '';
            cell.hidden = true;
          }
        });

        this.updateCardVisibility();
      }

      updateCardVisibility() {
        const hasVisibleProductCells = this.productCells.length > 0;
        const hasVisibleVariantCells = Array.from(this.variantCells).some((cell) => !cell.hidden);
        const shouldShow = hasVisibleProductCells || hasVisibleVariantCells;

        this.hidden = !shouldShow;
        if (this.container) this.container.hidden = !shouldShow;
      }
    }
  );
}
